import { memo, ReactNode } from 'react'
import { FloatingPortal } from '@floating-ui/react'
import { autoUpdate, ComputePositionConfig, flip, offset, ReferenceType, useFloating } from '@floating-ui/react-dom'
import cn from 'classnames'
import { useIsMobile } from 'hooks/media'
import { ISwitcher, useSwitcher } from 'hooks/useSwitcher'
import { Menu } from 'shared/components/Menu'
import { ModalMobile } from 'src/components/extend'

export type TDropdownChildrenRenderProps = ISwitcher & { ref: (node: ReferenceType | null) => any }

export interface IDropdownProps {
  children: (callback: TDropdownChildrenRenderProps) => ReactNode
  dropdownRender: (switcher: ISwitcher) => ReactNode
  placement: ComputePositionConfig['placement']
  id?: string
  isCloseOnMobileOutsideClick?: boolean
  isFullscreenInMobile?: boolean
  isShowOverlay?: boolean
  menuClassName?: string
  mobileLabel?: string
  zIndex?: number
}

export const Dropdown = memo(function Dropdown({
  id,
  children,
  dropdownRender,
  mobileLabel,
  menuClassName,
  placement,
  isFullscreenInMobile,
  isShowOverlay = true,
  isCloseOnMobileOutsideClick,
  zIndex = 8888
}: IDropdownProps) {
  const switcher = useSwitcher(false)
  const { active, handleClose } = switcher
  const dropdownContent = dropdownRender(switcher)

  const { x, y, refs, strategy } = useFloating({
    placement,
    middleware: [offset(), flip()],
    whileElementsMounted: autoUpdate
  })

  const isMobile = useIsMobile()

  return (
    <>
      {children({ ref: refs.setReference, ...switcher })}

      {active && (
        <FloatingPortal>
          {isShowOverlay && (
            <div
              onClick={handleClose}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex
              }}
            />
          )}
          {isMobile && isFullscreenInMobile ? (
            <ModalMobile
              className="fixed inset-0"
              header={mobileLabel}
              id={id}
              isCloseOnOutsideClick={isCloseOnMobileOutsideClick}
              onClose={handleClose}
              style={{ zIndex: zIndex + 1 }}
            >
              {dropdownContent}
            </ModalMobile>
          ) : (
            <div
              ref={refs.setFloating}
              data-placement={placement}
              data-test-id="dropdown-popper"
              id={id}
              style={{
                position: strategy,
                inset: '0 auto auto 0',
                transform: `translate(${x ?? 0}px, ${y ?? 0}px)`,
                zIndex: zIndex + 1
              }}
            >
              <Menu
                className={cn(
                  'mt-[3px] w-max max-w-[280px] animate-[acc-slide-down_150ms_1] md:max-w-[400px]',
                  menuClassName
                )}
              >
                {dropdownContent}
              </Menu>
            </div>
          )}
        </FloatingPortal>
      )}
    </>
  )
})
